import { onAuthStateChanged } from 'firebase/auth';
import { create } from 'zustand';
import { analytics, auth, firestore } from '../context/firebase.js';
import {
  getClaimSettings,
  getDeliverySettings,
  getRequiredLicenseAgreements,
  getUserInfo,
  subscribeToSuppliers,
  subscribeToStockFields,
} from '../shared_components/utils/FirebaseUtils.js';
import { logEvent } from 'firebase/analytics';
import { addDoc, collection, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import i18n from '../i18n/i18n.ts';
import {
  getActiveGlobalLabelsets,
  getLabelsetProfileByClientRole,
  constructLabelsetGroups,
} from '../shared_components/utils/FirebaseLabelsetsUtils.js';

export const useAuthStore = create((set, get) => {
  async function getUserData(user) {
    const currentUserNew = await getUserInfo(firestore, user.uid);
    currentUserNew.uid = user.uid;

    if (currentUserNew.company) {
      subscribeToSuppliers(firestore, currentUserNew.company.company, (newSuppliers) => {
        set((state) => ({ ...state, suppliers: newSuppliers }));
      });

      subscribeToStockFields(firestore, currentUserNew.company.company, (newStockFields) => {
        set((state) => ({ ...state, stockFields: newStockFields }));
      });

      // Fetch static data in parallel
      const [
        globalLabelsetsResult,
        defaultLabelsetProfileResult,
        requiredLicenseAgreementsResult,
        deliverySettingsResult,
        claimSettingsResult,
      ] = await Promise.all([
        getActiveGlobalLabelsets(firestore),
        getLabelsetProfileByClientRole(firestore, currentUserNew.company, 'default'),
        getRequiredLicenseAgreements(firestore),
        getDeliverySettings(firestore, currentUserNew.company.company),
        getClaimSettings(firestore, currentUserNew.company.company),
      ]);

      // Construct labelset groups
      const defaultLabelsetsResult = defaultLabelsetProfileResult?.labelsets || {};
      const [globalLabelsetGroupsResult, defaultLabelsetGroupsResult] = await Promise.all([
        constructLabelsetGroups(firestore, globalLabelsetsResult || {}),
        constructLabelsetGroups(firestore, defaultLabelsetsResult || {}),
      ]);

      set((state) => ({
        ...state,
        globalLabelsets: globalLabelsetsResult,
        defaultLabelsets: defaultLabelsetsResult,
        globalLabelsetGroups: globalLabelsetGroupsResult,
        defaultLabelsetGroups: defaultLabelsetGroupsResult,
        requiredLicenseAgreements: requiredLicenseAgreementsResult,
        deliverySettings: deliverySettingsResult,
        claimSettings: claimSettingsResult,
      }));
    }

    set((state) => ({ ...state, currentUser: currentUserNew, userLoaded: true }));
    if (currentUserNew?.info?.languageCode) {
      i18n.changeLanguage(currentUserNew.info.languageCode);
    }
  }

  function authInit() {
    onAuthStateChanged(auth, (user) => {
      set((state) => ({ ...state, authUser: user, userLoaded: false }));
      if (user) {
        getUserData(user);
      } else {
        set((state) => ({ ...state, currentUser: {} }));
      }
    });
  }

  return {
    authUser: {},
    currentUser: {
      uid: null,
      info: { languageCode: 'de' },
      company: { company_displayname: '', company: '', shift_time: 0 },
      devices: {},
      device_id: null,
      filter_observation_classes: [],
    },
    userLoaded: false,
    deliverySettings: {},
    claimSettings: {},
    requiredLicenseAgreements: [],
    suppliers: {},
    stockFields: {},
    logEventCustom: (eventName, eventParams) => {
      /* Log a dashboard event to analytics including our custom event handling */
      // Log the event to Firebase Analytics

      const currentUser = get().currentUser;

      logEvent(analytics, eventName, eventParams);
      // Log the event directly to our database including user ID
      addDoc(collection(firestore, 'event_logs'), {
        event_name: eventName,
        event_params: eventParams,
        timestamp: serverTimestamp(),
        user: currentUser.uid,
        client: currentUser.company.company,
        source: 'assistant',
      });
    },
    changeActiveDevice: (device_id) => {
      const currentUser = get().currentUser;
      /* Change the active device displayed in the dashboard */
      set((state) => ({ ...state, currentUser: { ...state.currentUser, device_id: device_id } }));
      updateDoc(doc(firestore, 'users', currentUser.uid), { selected_device_id: device_id });
    },
    authInit,
  };
});

useAuthStore.subscribe(
  (state) => state.currentUser?.uid,
  (uid, previousUid) => {
    if (uid && uid !== previousUid) {
      const currentUser = useAuthStore.getState().currentUser;
      updateDoc(doc(firestore, 'users', currentUser.uid), { last_login: serverTimestamp() });
      useAuthStore.getState().logEventCustom('login', {});
    }
  },
);
